import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const resolveApiBase = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '';
  if (typeof window === 'undefined') return envBase || '';
  if (!envBase) return '';
  if (process.env.NODE_ENV !== 'production' && envBase.replace(/\/$/, '') === window.location.origin.replace(/\/$/, '')) {
    return '';
  }
  return envBase;
};

const API_BASE = resolveApiBase();
const VISITOR_KEY = 'hv.visitor.id';
const SESSION_KEY = 'hv.visitor.session';
const SESSION_STARTED_KEY = 'hv.visitor.session.started';
const SESSION_TTL_MS = 30 * 60 * 1000;

const randomId = (prefix) => {
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : null;
  if (cryptoObj?.randomUUID) return `${prefix}_${cryptoObj.randomUUID()}`;
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

const readStorage = (storage, key) => {
  try {
    return storage?.getItem(key) || '';
  } catch (_) {
    return '';
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage?.setItem(key, value);
  } catch (_) {
    // Ignore storage failures; tracking should never block the app.
  }
};

const getVisitorIds = () => {
  if (typeof window === 'undefined') return { visitorId: '', sessionId: '' };
  const now = Date.now();
  const visitorId = readStorage(window.localStorage, VISITOR_KEY) || randomId('v');
  writeStorage(window.localStorage, VISITOR_KEY, visitorId);

  const startedAt = Number(readStorage(window.sessionStorage, SESSION_STARTED_KEY) || 0);
  const storedSession = readStorage(window.sessionStorage, SESSION_KEY);
  const sessionExpired = !startedAt || now - startedAt > SESSION_TTL_MS;
  const sessionId = storedSession && !sessionExpired ? storedSession : randomId('s');
  writeStorage(window.sessionStorage, SESSION_KEY, sessionId);
  writeStorage(window.sessionStorage, SESSION_STARTED_KEY, String(now));
  return { visitorId, sessionId };
};

const trimText = (value, limit = 240) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, limit);

const getElementDetails = (target) => {
  const element = target?.closest?.('a,button,[role="button"],input,select,textarea,label,[data-track]');
  if (!element) return null;
  const label =
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    element.getAttribute('data-track') ||
    element.innerText ||
    element.value ||
    element.name ||
    element.id ||
    element.tagName;
  const href = element.getAttribute('href') || element.getAttribute('data-href') || '';
  return {
    elementLabel: trimText(label),
    elementTarget: trimText(href || element.id || element.name || element.tagName, 500),
    metadata: {
      tag: String(element.tagName || '').toLowerCase(),
      type: element.getAttribute('type') || '',
    },
  };
};

export default function VisitorActivityTracker() {
  const location = useLocation();
  const ids = useMemo(getVisitorIds, []);
  const lastPageRef = useRef('');

  const sendEvent = (payload) => {
    if (!ids.visitorId || !ids.sessionId || typeof window === 'undefined') return;
    const body = JSON.stringify({
      visitorId: ids.visitorId,
      sessionId: ids.sessionId,
      path: `${window.location.pathname}${window.location.search}`,
      title: document.title,
      referrer: document.referrer,
      ...payload,
    });
    const url = `${API_BASE}/api/analytics/track`;
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body,
      keepalive: true,
    }).catch(() => {});
  };

  useEffect(() => {
    const pageKey = `${location.pathname}${location.search}`;
    if (lastPageRef.current === pageKey) return;
    lastPageRef.current = pageKey;
    window.setTimeout(() => {
      sendEvent({ eventType: 'page_view', path: pageKey, title: document.title });
    }, 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    let lastClickAt = 0;
    const handleClick = (event) => {
      const now = Date.now();
      if (now - lastClickAt < 250) return;
      const details = getElementDetails(event.target);
      if (!details) return;
      lastClickAt = now;
      sendEvent({ eventType: 'click', ...details });
    };

    const handleSubmit = (event) => {
      const form = event.target;
      sendEvent({
        eventType: 'form_submit',
        elementLabel: trimText(form?.getAttribute?.('aria-label') || form?.name || form?.id || 'Form'),
        elementTarget: trimText(form?.action || ''),
        metadata: { formId: form?.id || '', formName: form?.name || '' },
      });
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
